import { useCallback, useEffect, useMemo, useState } from "react";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { splitStationRows, summarizeStationMonitorRows } from "../util/stationMonitor";

// #3190 (station-monitor workstream) -- one call, GET /show/:showId/station-monitor
// (#3189), returns display-ready station rows (per-show station numbers, scribe
// display names, work segments, snapshot counts and reconcile state). This hook
// no longer fetches presence and snapshots and joins them in the browser: the
// server owns that join. The show-wide summary the dashboard strip needs is
// derived from the SAME rows (util/stationMonitor.js), so the strip and the
// page cannot disagree.
//
// Not polled: this is a tent-glances-at-it page, so it fetches on mount/showId
// change and on an explicit refetch(). Read only (#3268): reconcile review
// lives in the Review Queue, so this hook carries no write actions.
// #3427: a stuck-station banner that only knew the state at mount would never
// appear (or clear) while staff sit on the page, so the hook now also re-reads
// quietly every STATION_MONITOR_POLL_MS. The quiet re-read keeps the rows on
// screen (no loading flash) and keeps the last good rows if one poll fails.
//
// #3507: `rows` is every station the server knows. `visibleRows` is what the
// default view, the banners and the strip count: stations in contact in the last
// 2 hours, plus ANY station holding work (never hidden for age), minus cleared
// ones. `olderRows` is the rest. The summary is built from `visibleRows` only,
// so a hidden stale or cleared station can never raise a warning. clearStation /
// restoreStation are the admin's Clear station and its Restore (score-api
// requires assign_scribes); both refetch so the page and strip move together.
export const STATION_MONITOR_POLL_MS = 30000;

// #3508: a station marked "This is a test tablet" at setup is left out of
// `visibleRows`, `olderRows`, the summary and so every banner and the strip,
// unless `showTest` is on. `testRows` is how many there are, for the switch.
export function useStationMonitor(showId, { pollMs = STATION_MONITOR_POLL_MS, showTest = false } = {}) {
  const scoreApi = useScoreApi();
  const [fetched, setFetched] = useState({ rows: [], at: 0, loading: true, error: null });

  const refetch = useCallback(async ({ quiet = false } = {}) => {
    if (!showId) {
      setFetched({ rows: [], at: 0, loading: false, error: null });
      return;
    }
    if (!quiet) setFetched((s) => ({ ...s, loading: true, error: null }));
    try {
      const rows = unwrap(await scoreApi.stationMonitor.getStationMonitor(showId)) ?? [];
      setFetched({ rows, at: Date.now(), loading: false, error: null });
    } catch (err) {
      if (quiet) return;
      setFetched({ rows: [], at: 0, loading: false, error: err });
    }
  }, [scoreApi, showId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    if (!showId || !pollMs) return undefined;
    const id = setInterval(() => refetch({ quiet: true }), pollMs);
    return () => clearInterval(id);
  }, [refetch, showId, pollMs]);

  const state = useMemo(() => {
    const counted = fetched.rows.filter((r) => showTest || r.isTest !== true);
    const { visible, older } = splitStationRows(counted, fetched.at || Date.now());
    return {
      rows: counted,
      visibleRows: visible,
      olderRows: older,
      testRows: fetched.rows.filter((r) => r.isTest === true),
      summary: fetched.at ? summarizeStationMonitorRows(visible) : null,
      loading: fetched.loading,
      error: fetched.error,
    };
  }, [fetched, showTest]);

  const clearStation = useCallback(
    async (deviceId) => {
      await scoreApi.stationMonitor.clearStationMonitorStation(showId, deviceId);
      await refetch({ quiet: true });
    },
    [scoreApi, showId, refetch],
  );
  const restoreStation = useCallback(
    async (deviceId) => {
      await scoreApi.stationMonitor.restoreStationMonitorStation(showId, deviceId);
      await refetch({ quiet: true });
    },
    [scoreApi, showId, refetch],
  );

  // #3518: Clear The Machine. Asks the tablet to upload everything, then wipe itself and
  // become a new station. Different from clearStation above, which only hides a dead
  // row. Resolves to { requested, command }: requested:false means this station already
  // cleared (not an error). Refetches so the row shows the new state.
  const requestMachineClear = useCallback(
    async (deviceId) => {
      const data = unwrap(await scoreApi.stationMonitor.requestStationMachineClear(showId, deviceId));
      await refetch({ quiet: true });
      return { requested: data?.requested === true, command: data?.command ?? null };
    },
    [scoreApi, showId, refetch],
  );

  // #3520: rename a station (same GUID, the old name goes into the server's name
  // history). checkName is the 7-day same-name warning: the other recently seen
  // stations carrying that name. It only warns; renameStation still allows it.
  const checkStationName = useCallback(
    async (name, stationId) => {
      const data = unwrap(await scoreApi.stations.checkStationName(name, stationId));
      return Array.isArray(data?.matches) ? data.matches : [];
    },
    [scoreApi],
  );
  const renameStation = useCallback(
    async (stationId, name) => {
      await scoreApi.stations.renameStation(stationId, { name });
      await refetch({ quiet: true });
    },
    [scoreApi, refetch],
  );

  return { ...state, refetch, clearStation, restoreStation, requestMachineClear, checkStationName, renameStation };
}

export default useStationMonitor;
